/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.4.0 - useTimeZone Hook
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useTimeZone.ts
 * @description Hook para gestión de zonas horarias
 * @version 1.0.0
 * @date 2025-01-27
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useCallback, useMemo } from 'react';
import { TimeZoneInfo } from '../types/scheduler.types';
import {
  TIMEZONES,
  detectUserTimezone,
  getTimezoneInfo,
  convertToTimezone,
  getTimeInTimezone,
  getTimezonesByRegion,
  searchTimezones,
  createUTCFromLocal,
  convertUTCToLocal,
  generateSchedulePreview,
  isValidScheduleTime
} from '../types/timezoneUtils';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface UseTimeZoneReturn {
  // Current timezone
  currentTimezone: string;
  currentTimezoneInfo: TimeZoneInfo | undefined;
  
  // All timezones
  timezones: TimeZoneInfo[];
  
  // Filtered timezones
  timezonesByRegion: (region: 'americas' | 'europe' | 'asia' | 'oceania' | 'africa') => TimeZoneInfo[];
  searchResults: TimeZoneInfo[];
  
  // Actions
  setTimezone: (timezone: string) => void;
  resetToDetected: () => void;
  search: (query: string) => void;
  clearSearch: () => void;
  
  // Conversion helpers
  convertDateToTimezone: (date: Date, timezone: string) => string;
  getTimeInTz: (date: Date, timezone: string) => string;
  createUTCDate: (localDate: string, localTime: string, timezone: string) => Date;
  convertToLocal: (utcDate: Date, timezone: string) => { date: string; time: string };
  
  // Validation and preview
  generatePreview: (localDate: string, localTime: string, timezone: string) => string;
  validateScheduleTime: (localDate: string, localTime: string, timezone: string) => {
    valid: boolean;
    message?: string;
  };
  
  // Search state
  searchQuery: string;
  isSearching: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useTimeZone = (): UseTimeZoneReturn => {
  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [currentTimezone, setCurrentTimezone] = useState<string>(() => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('scheduler:timezone');
    return saved || detectUserTimezone();
  });
  
  const [searchQuery, setSearchQuery] = useState('');

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════════════
  
  const currentTimezoneInfo = useMemo(() => {
    return getTimezoneInfo(currentTimezone);
  }, [currentTimezone]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return TIMEZONES;
    }
    return searchTimezones(searchQuery);
  }, [searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  // ═══════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════
  
  const setTimezone = useCallback((timezone: string) => {
    setCurrentTimezone(timezone);
    // Persist to localStorage
    localStorage.setItem('scheduler:timezone', timezone);
  }, []);

  const resetToDetected = useCallback(() => {
    const detected = detectUserTimezone();
    setCurrentTimezone(detected);
    localStorage.setItem('scheduler:timezone', detected);
  }, []);

  const search = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // REGION FILTER
  // ═══════════════════════════════════════════════════════════════════════
  
  const timezonesByRegion = useCallback((
    region: 'americas' | 'europe' | 'asia' | 'oceania' | 'africa'
  ): TimeZoneInfo[] => {
    return getTimezonesByRegion(region);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // CONVERSION HELPERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const convertDateToTimezone = useCallback((date: Date, timezone: string): string => {
    return convertToTimezone(date, timezone);
  }, []);

  const getTimeInTz = useCallback((date: Date, timezone: string): string => {
    return getTimeInTimezone(date, timezone);
  }, []);

  const createUTCDate = useCallback((
    localDate: string,
    localTime: string,
    timezone: string
  ): Date => {
    return createUTCFromLocal(localDate, localTime, timezone);
  }, []);

  const convertToLocal = useCallback((
    utcDate: Date,
    timezone: string
  ): { date: string; time: string } => {
    return convertUTCToLocal(utcDate, timezone);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // VALIDATION AND PREVIEW
  // ═══════════════════════════════════════════════════════════════════════
  
  const generatePreview = useCallback((
    localDate: string,
    localTime: string,
    timezone: string
  ): string => {
    return generateSchedulePreview(localDate, localTime, timezone);
  }, []);

  const validateScheduleTime = useCallback((
    localDate: string,
    localTime: string,
    timezone: string
  ): { valid: boolean; message?: string } => {
    return isValidScheduleTime(localDate, localTime, timezone);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════
  
  return {
    // Current timezone
    currentTimezone,
    currentTimezoneInfo,
    
    // All timezones
    timezones: TIMEZONES,
    
    // Filtered timezones
    timezonesByRegion,
    searchResults,
    
    // Actions
    setTimezone,
    resetToDetected,
    search,
    clearSearch,
    
    // Conversion helpers
    convertDateToTimezone,
    getTimeInTz,
    createUTCDate,
    convertToLocal,
    
    // Validation and preview
    generatePreview,
    validateScheduleTime,
    
    // Search state
    searchQuery,
    isSearching
  };
};

export default useTimeZone;
